/*
Makes call to the server to get the mini-cart data from either the cart in session or the persisted cart,
then updates the local storage and refreshes the display.
Skip while on givingCart pages.
 */
import { MiniCartDetails } from './miniCartDetails';
import { formatCurrencyV2 } from '../../common/formatCurrency';
import { type AjaxResponse } from '../../common/types/AjaxResponse';

const KEYS = {
    MINI_CART_LOCALSTORAGE_TIMESTAMP: 'miniCartUpdated',
    MINI_CART_DATA: 'miniCartDetails',
};

type MiniCartJsonpResponse = AjaxResponse<{ cart?: MiniCartDetails; accountCredits?: unknown }>;

export const refreshMiniCartFromServer = function (callback?: () => void) {
    if (location.pathname.indexOf('givingCart.html') > -1) {
        return;
    }
    const e = document.createElement('script');
    e.src = secureBaseURL + 'donors/mini_cart.html?callback=' + ((callback) || 'refreshMiniCartFromServerCallback');
    e.type = 'text/javascript';
    document.getElementsByTagName('head')[0].appendChild(e);
};

// JSONP callback (and therefore cannot be removed from `window`)
export const refreshMiniCartFromServerCallback = function (obj?: MiniCartJsonpResponse) {
    const miniCartDetails = obj?.data?.cart;
    updateMiniCartLocalStorage(miniCartDetails);

    const accountCredits = obj?.data?.accountCredits;
    if (accountCredits != null) {
        window.eraseCookie('accountCredits');
        window.saveAndDisplayAccountCredits(accountCredits);
    } else {
        window.displayAccountCreditsTip();
    }
    updateMiniCartDisplay(obj);
    $(window).trigger('miniCartUpdated');
};

/*
Retrieves the mini-cart data from either the server or from local storage and then refreshes the display.
When retrieving the data from the server, the local storage data is also updated.
Called by keepalive.jsp (and therefore cannot be removed from `window`)
 */
export const refreshMiniCartFromServerOrLocalStorage = function () {
    if (location.pathname.indexOf('givingCart.html') > -1) {
        return;
    }
    if (!window.isNonBlankCookie('cartUUID')) {
        // Skip checking localStorage if there's no cartUUID (persist-the-cart) cookie.
        //
        // We do this because if you have no cartUUID cookie, *probably* you've already checked out your cart but you're
        // on WWW so your localStorage doesn't know that. If you have real cart-contents but no cartUUID cookie, either
        // you're logged in (and we'll restore your cartUUID cookie soon), or you've lost the one thing we can use to
        // connect non-logged-in users to their carts.
        return;
    }

    if (localStorageStateLooksStale()) {
        refreshMiniCartFromServer();
        // Don't bother updating the header on the basis of stale localStorage
        return;
    }

    const miniCartDetailsPromise = getMiniCartDetailsFromLocalStorageData();

    miniCartDetailsPromise.then(function (miniCartDetails) {
        if (miniCartDetails.hasItems) {
            const obj = { data: { cart: miniCartDetails } };
            updateMiniCartDisplay(obj);
        }

        // Even a "fresh" localStorage may be out of date if the user changes devices etc. - hit the server for
        // fully-updated cart-state.
        //
        // Note that because `updateMiniCartDisplay` cannot *remove* an existing cart-icon, a refresh that finds that
        // the cart is empty now will not update the page correctly. The next page load will fix this.
        refreshMiniCartFromServer();
    });
};

/**
 * There's no direct way for JS on WWW to know if `secure` has updated the minicart data in localStorage (or vice versa).
 * We're hacking something together with timestamps and cookies; if the timestamp in localStorage is older than the cookie,
 * you should hit the server to refresh your minicart data.
 */
const localStorageStateLooksStale = (): boolean => {
    const cookieTimestamp = window.readCookie(KEYS.MINI_CART_LOCALSTORAGE_TIMESTAMP);
    const localStorageTimestamp = localStorage.getItem(KEYS.MINI_CART_LOCALSTORAGE_TIMESTAMP);

    if (!cookieTimestamp || !localStorageTimestamp) {
        return true;
    }

    return Number(cookieTimestamp) > Number(localStorageTimestamp);
};

/*
Given a mini-cart details object, updates the local storage and refreshes the display.
Used by other JS and JSPs. JSP usage means this cannot be removed from `window`.
 */
export const refreshMiniCartFromDetailsObject = function (miniCartDetails: MiniCartDetails) {
    updateMiniCartLocalStorage(miniCartDetails);
    window.displayAccountCreditsTip();
    const miniCartObj = { data: { cart: miniCartDetails } };
    updateMiniCartDisplay(miniCartObj);
};

/**
 * Updates the mini-cart local storage from a miniCartDetails object.
 */
function updateMiniCartLocalStorage(miniCartDetails: MiniCartDetails | nullish) {
    window.eraseCookie('cartGiftCodeAmount');

    if (miniCartDetails) {
        const encodedMiniCartData = window.btoa(encodeURIComponent(JSON.stringify(miniCartDetails)));

        const existingMiniCartData = localStorage.getItem(KEYS.MINI_CART_LOCALSTORAGE_TIMESTAMP);
        if (existingMiniCartData !== encodedMiniCartData) {
            const timeString = Date.now().toString();
            localStorage.setItem(KEYS.MINI_CART_LOCALSTORAGE_TIMESTAMP, timeString);
            window.createCookie(KEYS.MINI_CART_LOCALSTORAGE_TIMESTAMP, timeString);
        }

        localStorage.setItem(KEYS.MINI_CART_DATA, encodedMiniCartData);

        // The cartGiftCodeAmount cookie is also created from the gift code redemption page
        // and I'm leaving it as a standalone cookie to be consistent.
        window.createCookie('cartGiftCodeAmount', miniCartDetails.cartGiftCodeAmount.toString());
    }
}

/**
 * Updates the mini-cart display, given a "wrapped" version of the miniCartDetails object
 */
function updateMiniCartDisplay(obj?: MiniCartJsonpResponse) {
    const showCartBannerWithMessage = function (cartItems: number, message: string) {
        $('#mainHeader, #mobile-header').addClass('cart-on');
        $('.mini-cart').attr('data-value', cartItems);
        cartItems > 0 && $('.mini-cart').addClass('enabled');
        $('.cart-message').html(message);
    };
    const rawDollarAmount = function (dollarString: string | undefined) {
        if (!dollarString) {
            return 0;
        }

        const cleanedNumericString = dollarString.replace(/[$,]/g, '');
        return Number(cleanedNumericString);
    };

    let cart: Partial<MiniCartDetails> | undefined = obj?.data?.cart;
    if (!cart) {
        cart = {};
        cart.cartItems = 0;
    }
    const cartHasSpendingItems = Number(cart.cartItems) > 0;

    if (pageName === 'Checkout (View)') {
        $('.mini-cart').addClass('checkout');
    }

    if (!cartHasSpendingItems) {
        if (cart.promoCode) {
            showPromoCodeRedemptionBanner(cart.promoCode);
        }
    } else { // show the banner with the appropriate message
        const hasPositiveSpendingAmount = rawDollarAmount(cart.cartTotal?.toString());
        if (hasPositiveSpendingAmount) {
            // hasPositiveSpendingAmount guarantees cartTotal is non-nullish, but TS can't infer that
            const formattedTotal = formatCurrencyV2(cart.cartTotal!, 'HIDE_ZERO_CENTS');
            let bannerMessage = `${formattedTotal} for ${cart.cartItems} classroom${cart.cartItems === 1 ? '' : 's'}`;

            const teacherScreenNameIsSet = !!cart.teacherScreenName;
            if (teacherScreenNameIsSet) {
                bannerMessage = `${formattedTotal} for ${cart.teacherScreenName}'s classroom`;
            }

            if (cart.cartItems != null) {
                showCartBannerWithMessage(cart.cartItems, bannerMessage);
            }
        }
    }

    $(window).trigger('cartBannerUpdated');
}

/*
Returns a mini-cart details object from the encoded JSON data stored in local storage, or from the cookie if
the user is using Safari.

Note that the local storage can go out of sync in cross-device scenarios. If that is ever an issue,
we could have a version of this method that always gets the MiniCartDetails from server data.

This function was incorrectly calling dcodeURIComponent (missing "e") in the else branch for a very long time.
That should've meant it wasn't really doing anything and just erroring out.
Sue fixed the typo in the if branch, but not the else
https://github.com/donorsChoose/donorschoose-web/commit/09c3fbc5b4367967cfef6350e2481b6897993dbb
Is it even needed?
 */
export const getMiniCartDetailsFromLocalStorageData = function (): Promise<MiniCartDetails> {
    return new Promise(function (resolve) {
        const rawResultString = localStorage.getItem(KEYS.MINI_CART_DATA);

        if (rawResultString) {
            resolve(new MiniCartDetails(window.atob(decodeURIComponent(rawResultString))));
        } else {
            resolve(new MiniCartDetails(null));
        }
    });
};

/**
 * Blow away minicart state. Called on signout and after successful checkout (specifically, when we run
 * conversionTracking.jspf). Cannot be removed from `window` due to JSP usage.
 */
export const resetMiniCart = function () {
    localStorage.removeItem(KEYS.MINI_CART_DATA);
    const timestampString = Date.now().toString();
    localStorage.setItem(KEYS.MINI_CART_LOCALSTORAGE_TIMESTAMP, timestampString);
    window.createCookie(KEYS.MINI_CART_LOCALSTORAGE_TIMESTAMP, timestampString);

    updateMiniCartDisplay({ data: { cart: new MiniCartDetails(null) } });
};

/**
 * Modifies the DOM to tell the user that they've redeemed promo-code ${promoCode}. Relies on HTML elements' IDs.
 */
function showPromoCodeRedemptionBanner(promoCode: string) {
    $('#promo-code-redemption-message').html('Your promo code <strong>' + promoCode + '</strong> has been successfully applied and will be reflected in checkout.');
    $('#promo-code-redemption-banner').show();
}
